import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, voiceApi, voiceComplianceApi } from '../lib/api';

export interface PlatformHealthData {
  period: { days: number; startDate: string };
  messaging: {
    totalMessages: number;
    totalMessagesPrev: number;
    changePercent: number;
    deliveryRate: number;
    readRate: number;
    failedCount: number;
    totalCostUsd: string;
    byChannel: Array<{ channel: string; count: number; costUsd: string }>;
    byStatus: Array<{ status: string; count: number }>;
    dailyTrend: Array<{ date: string; total: number; delivered: number; failed: number }>;
  };
  voice: {
    totalCalls: number;
    totalCallsPrev: number;
    changePercent: number;
    answerRate: number;
    avgDurationSeconds: number;
    totalDurationSeconds: number;
    totalCostUsd: string;
    byDisposition: Array<{ disposition: string; count: number }>;
  };
  pipeline: {
    newLeads: number;
    newLeadsPrev: number;
    changePercent: number;
    staleLeads: number;
    unassignedLeads: number;
    bySource: Array<{ source: string; count: number }>;
    dailyTrend: Array<{ date: string; count: number }>;
  };
  users: {
    totalActive: number;
    activeLastWeek: number;
    activeLastMonth: number;
    inactiveRisk: number;
    churnRiskPercent: number;
    activeOrgs7d: number;
    activeOrgs30d: number;
  };
  toolActions: {
    total: number;
    pendingNow: number;
    rejectionRate: number;
    byStatus: Array<{ status: string; count: number; avgDurationMs: number }>;
  };
  workflows: {
    running: number;
    succeeded: number;
    failed: number;
  };
  alerts: {
    whatsappWindowsExpiring: number;
    staleLeads: number;
    unassignedLeads: number;
    pendingToolApprovals: number;
    failedMessages: number;
  };
  email: {
    totalSent: number;
    totalSentPrev: number;
    changePercent: number;
    deliveryRate: number;
    failedCount: number;
    totalCostUsd: string;
    byStatus: Array<{ status: string; count: number }>;
    dailyTrend: Array<{ date: string; total: number; delivered: number; failed: number }>;
  };
  ai?: {
    totalCostUsd: string;
    totalExecutions: number;
    byProvider: Array<{
      provider: string;
      costUsd: string;
      executionCount: number;
      inputTokens: number;
      outputTokens: number;
    }>;
    byModel: Array<{
      model: string;
      provider: string;
      costUsd: string;
      executionCount: number;
      inputTokens: number;
      outputTokens: number;
    }>;
  };
  providerCosts: Array<{
    provider: string;
    costUsd: string;
    count: number;
    source: string;
  }>;
  platformCost: {
    aiCostUsd: string;
    messagingCostUsd: string;
    voiceCostUsd: string;
    externalServicesCostUsd: string;
    totalCostUsd: string;
  };
}

export interface ProviderHealthReport {
  status: 'ok' | 'degraded' | 'down';
  checks: Array<{
    name: string;
    status: 'ok' | 'degraded' | 'down';
    latencyMs?: number;
    message?: string;
  }>;
  timestamp: string;
}

export function useProviderHealth() {
  return useQuery<ProviderHealthReport>({
    queryKey: ['admin', 'provider-health'],
    queryFn: async () => {
      const { data } = await api.get('/provider-health');
      return data;
    },
    refetchInterval: 60_000,
  });
}

export interface HealthScoreData {
  overall: number;
  status: 'healthy' | 'degraded' | 'critical';
  domains: Array<{
    domain: string;
    score: number;
    status: 'healthy' | 'degraded' | 'critical';
    detail: string;
  }>;
}

export function useHealthScore(days?: number) {
  return useQuery<HealthScoreData>({
    queryKey: ['admin', 'health-score', days],
    queryFn: async () => {
      const { data } = await api.get('/health-score', { params: days ? { days } : undefined });
      return data;
    },
  });
}

export interface HealthAlertConfig {
  recipients: string[];
  slackWebhookUrl?: string;
}

export function useHealthAlertConfig() {
  return useQuery<HealthAlertConfig>({
    queryKey: ['admin', 'health-alerts', 'config'],
    queryFn: async () => {
      const { data } = await api.get('/health-alerts/config');
      return data;
    },
  });
}

export function useUpdateHealthAlertConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (config: HealthAlertConfig) => {
      const { data } = await api.put('/health-alerts/config', config);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'health-alerts', 'config'] });
    },
  });
}

export function useSendHealthAlert() {
  return useMutation({
    mutationFn: async (payload?: { force?: boolean; days?: number }) => {
      const { data } = await api.post('/health-alerts/send', payload ?? {});
      return data as {
        dispatched: boolean;
        reason?: string;
        channels: { slack: string; email: string };
      };
    },
  });
}

export function usePlatformHealth(params?: { days?: number; organizationId?: string }) {
  return useQuery<PlatformHealthData>({
    queryKey: ['admin', 'platform-health', params],
    queryFn: async () => {
      const { data } = await api.get('/platform-health', { params });
      return data;
    },
    refetchInterval: 60_000,
  });
}

export interface OverviewData {
  totalOrganizations: number;
  totalUsers: number;
  totalLeads: number;
  monthlyAiCost: string;
  planDistribution: { plan: string; count: number }[];
  recentOrganizations: {
    id: string;
    name: string;
    plan: string;
    status: string;
    planExpiredAt: string | null;
    memberCount: number;
    leadCount: number;
    createdAt: string;
  }[];
}

export interface Organization {
  id: string;
  name: string;
  plan: string;
  status: string;
  planExpiredAt: string | null;
  memberCount: number;
  leadCount: number;
  totalAiCost: string;
  createdAt: string;
}

export interface OrganizationCredits {
  grantedCredits: number;
  usedCredits: number;
  availableCredits: number;
  periodStart: string;
  periodEnd: string;
}

export interface OrganizationDetail extends Organization {
  activeMembers: number;
  activeAgents: number;
  conversationStack?: 'convai' | 'relay';
  members: {
    id: string;
    name: string;
    email: string;
    role: string;
    lastActiveAt: string | null;
  }[];
  subscription: {
    plan: string;
    status: string;
    planExpiredAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    provider: string | null;
    billingCycle: 'monthly' | 'yearly' | null;
    externalCustomerId: string | null;
    externalSubscriptionId: string | null;
    cancelAtPeriodEnd: boolean;
    trialEndsAt: string | null;
    credits: OrganizationCredits | null;
  } | null;
  agentConfig: {
    id: string;
    name: string;
    model: string;
    totalExecutions: number;
    totalCost: string;
  }[];
  recentActivity: {
    id: string;
    type: string;
    description: string;
    createdAt: string;
    userName: string | null;
  }[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export function useOverview() {
  return useQuery<OverviewData>({
    queryKey: ['admin', 'overview'],
    queryFn: async () => {
      const { data } = await api.get('/overview');
      return data;
    },
  });
}

export function useOrganizations(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<Organization>>({
    queryKey: ['admin', 'organizations', params],
    queryFn: async () => {
      const { data } = await api.get('/organizations', { params });
      return data;
    },
  });
}

export function useOrganizationDetail(id: string) {
  return useQuery<OrganizationDetail>({
    queryKey: ['admin', 'organizations', id],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export interface CostAnalytics {
  period: { month: number; year: number };
  totalCostUsd: string;
  totalExecutions: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  dailyCosts: Array<{ date: string; costUsd: string; executionCount: number }>;
  byModel: Array<{
    model: string;
    costUsd: string;
    executionCount: number;
    inputTokens: number;
    outputTokens: number;
  }>;
  byProvider: Array<{ provider: string; costUsd: string; executionCount: number }>;
  topOrganizations: Array<{ id: string; name: string; costUsd: string; executionCount: number }>;
  externalCosts?: {
    totalCostUsd: string;
    entryCount: number;
    byService: Array<{
      provider: string;
      service: string;
      costUsd: string;
      units: number;
      entryCount: number;
    }>;
    byLead: Array<{
      leadId: string;
      leadName: string | null;
      costUsd: string;
      entryCount: number;
    }>;
  };
}

export function useCostAnalytics(params?: {
  month?: number;
  year?: number;
  organizationId?: string;
}) {
  return useQuery<CostAnalytics>({
    queryKey: ['admin', 'costs', params],
    queryFn: async () => {
      const { data } = await api.get('/costs', { params });
      return data;
    },
  });
}

export interface Subscription {
  id: string;
  organizationId: string;
  organizationName: string;
  plan: string;
  status: string;
  planExpiredAt: string | null;
  pendingPlan: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  provider: string | null;
  billingCycle: 'monthly' | 'yearly' | null;
  externalCustomerId: string | null;
  externalSubscriptionId: string | null;
  trialEndsAt: string | null;
  memberCount: number;
  leadCount: number;
}

export interface SubscriptionsResponse {
  data: Subscription[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useSubscriptions(params?: Record<string, unknown>) {
  return useQuery<SubscriptionsResponse>({
    queryKey: ['admin', 'subscriptions', params],
    queryFn: async () => {
      const { data } = await api.get('/subscriptions', { params });
      return data;
    },
  });
}

export interface BillingProviderBreakdown {
  provider: string;
  currency: string;
  mrr: number;
  arr: number;
  activeSubscriptions: number;
}

export interface BillingSummary {
  mrr: number;
  arr: number;
  activeSubscriptions: number;
  currency: string;
  collectedRevenue: Array<{ currencyCode: string; amount: number }>;
  collectedRevenueWindowDays: number;
  byProvider: BillingProviderBreakdown[];
}

export function useBillingSummary(params?: Record<string, unknown>) {
  return useQuery<BillingSummary>({
    queryKey: ['admin', 'billing', 'summary', params],
    queryFn: async () => {
      const { data } = await api.get('/billing/summary', { params });
      return data;
    },
  });
}

export interface BillingCheckoutReadiness {
  provider: 'wompi';
  environment: string;
  enabled: boolean;
  ready: boolean;
  eligible?: boolean;
  issues: string[];
  plans: Partial<Record<string, Partial<Record<'monthly' | 'yearly', boolean>>>>;
}

export function useBillingCheckoutReadiness(organizationId?: string) {
  const params = organizationId ? { organizationId } : undefined;
  return useQuery<BillingCheckoutReadiness>({
    queryKey: ['admin', 'billing', 'checkout-readiness', organizationId],
    queryFn: async () => {
      const { data } = await api.get('/billing/checkout-readiness', { params });
      return data;
    },
    staleTime: 30_000,
  });
}

export interface OrganizationPayment {
  id: string;
  amount: number;
  currencyCode: string;
  status: string;
  occurredAt: string;
  invoiceUrl: string | null;
  provider: string;
  externalPaymentId: string;
}

export interface OrganizationPaymentsResponse {
  data: OrganizationPayment[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export function useOrganizationPayments(orgId: string, params?: Record<string, unknown>) {
  return useQuery<OrganizationPaymentsResponse>({
    queryKey: ['admin', 'organizations', orgId, 'payments', params],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/payments`, { params });
      return data;
    },
    enabled: !!orgId,
  });
}

export function useUpdateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: { plan?: string; status?: string };
    }) => {
      return api.patch(`/organizations/${orgId}/subscription`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export interface GrantUsageCreditsResult {
  applied: boolean;
  ledgerEntryId: string;
  grantedCredits: number;
  availableCredits: number;
}

export function useGrantUsageCredits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      ...body
    }: {
      orgId: string;
      credits: number;
      reason: string;
      idempotencyKey: string;
    }) => {
      const { data } = await api.post<GrantUsageCreditsResult>(
        `/organizations/${orgId}/usage-credits/grant`,
        body,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export interface PlatformUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  platformRole: string | null;
  lastActiveAt: string | null;
  createdAt: string;
  organizations: Array<{ id: string; name: string; role: string }>;
}

export function useUsers(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<PlatformUser>>({
    queryKey: ['admin', 'users', params],
    queryFn: async () => {
      const { data } = await api.get('/users', { params });
      return data;
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      data,
    }: {
      userId: string;
      data: { platformRole: string | null };
    }) => {
      return api.patch(`/users/${userId}`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export type WaitlistStatus =
  | 'waiting'
  | 'invited'
  | 'activated'
  | 'reclaimed'
  | 'converted'
  | 'declined';

export interface WaitlistEntry {
  id: string;
  email: string;
  fullName: string | null;
  company: string | null;
  phone: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  status: WaitlistStatus;
  invitedAt: string | null;
  inviteExpiresAt: string | null;
  activatedAt: string | null;
  reminderSentAt: string | null;
  reclaimedAt: string | null;
  reclaimReason: string | null;
  convertedAt: string | null;
  organizationId: string | null;
}

export type FreeCapacityBlockedReason =
  | 'costing_untrustworthy'
  | 'no_cost_baseline'
  | 'negative_margin';

export interface FreeCapacitySnapshot {
  monthlyRevenueUsd: number;
  paidOrgCostUsd: number;
  marginUsd: number;
  giveawayBudgetUsd: number;
  costPerFreeOrgUsd: number;
  meanFreeOrgCostUsd: number;
  freeOrgCount: number;
  fundedSlots: number;
  /** The total the policy allows once floor and hard cap are applied. */
  allowedSlots: number;
  /** Free organizations plus the open waitlist invitations holding a slot. */
  occupiedSlots: number;
  /** Optional so the board still renders against a backend that predates it. */
  reservedByInvitations?: number;
  availableSlots: number;
  costWindowDays: number;
  unpricedPlans: string[];
  blockedReason?: FreeCapacityBlockedReason;
  policy: {
    giveawayShare: number;
    costPercentile: number;
    floor: number;
    hardCap: number;
    enforceSignupGate: boolean;
    inviteExpiryDays: number;
    inactivityWarnAfterDays: number;
    reminderGraceDays: number;
    promoteBatchSize: number;
  };
}

export function useFreeCapacity() {
  return useQuery<FreeCapacitySnapshot>({
    queryKey: ['admin', 'free-capacity'],
    queryFn: async () => {
      const { data } = await api.get('/free-capacity');
      return data;
    },
  });
}

export type FreeCapacityPolicyPatch = Partial<FreeCapacitySnapshot['policy']>;

export function useUpdateFreeCapacityPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: FreeCapacityPolicyPatch) => {
      const { data } = await api.patch('/free-capacity/policy', patch);
      return data as FreeCapacitySnapshot['policy'];
    },
    // The board exists to show what the policy does to the slot count, so the
    // snapshot must be refetched rather than left showing the old arithmetic.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'free-capacity'] });
    },
  });
}

export type WaitlistCounts = Record<WaitlistStatus, number>;

export function useWaitlist(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<WaitlistEntry> & { counts: WaitlistCounts }>({
    queryKey: ['admin', 'waitlist', params],
    queryFn: async () => {
      const { data } = await api.get('/waitlist', { params });
      return data;
    },
  });
}

export type AuditLevel = 'error' | 'warning' | 'info';
export type AuditSource = 'business_event' | 'activity' | 'admin';

export interface AuditLogEntry {
  id: string;
  adminId: string;
  adminName: string;
  action: string;
  level: AuditLevel;
  targetType: string;
  targetId: string;
  details: Record<string, unknown>;
  createdAt: string;
}

export interface AuditCatalogEntry {
  key: string;
  source: AuditSource;
  level: AuditLevel;
}

export function useAuditLogs(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<AuditLogEntry>>({
    queryKey: ['admin', 'audit-logs', params],
    queryFn: async () => {
      const { data } = await api.get('/audit-logs', { params });
      return data;
    },
  });
}

export function useAuditLogCatalog() {
  return useQuery<AuditCatalogEntry[]>({
    queryKey: ['admin', 'audit-logs', 'types'],
    queryFn: async () => {
      const { data } = await api.get('/audit-logs/types');
      return data;
    },
  });
}

export interface AgentPrompt {
  id: string | null;
  agentType: string;
  name: string;
  prompt: string;
  /** The compiled-in catalog prompt, so "Revert to Default" can restore it. */
  defaultPrompt: string;
  description: string | null;
  version: number;
  isActive: boolean;
  isDefault: boolean;
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AgentLogEntry {
  id: string;
  agentType: string;
  status: string;
  organizationId: string;
  organizationName: string;
  leadId: string | null;
  leadName: string | null;
  modelUsed: string | null;
  inputTokens: number;
  outputTokens: number;
  costUsd: string;
  durationMs: number | null;
  errorMessage: string | null;
  triggeredBy: string | null;
  toolCallCount?: number;
  toolNames?: string[];
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface AgentMetrics {
  period: { days: number; startDate: string };
  summary: {
    totalExecutions: number;
    completed: number;
    /** Completed runs that actually reached a model. */
    succeeded: number;
    /** Completed runs that degraded to rule-based output without reaching a model. */
    degraded: number;
    failed: number;
    running: number;
    queued: number;
    cancelled: number;
    failureRate: number;
    successRate: number;
    fallbackRate: number;
    avgDurationMs: number;
    totalCostUsd: string;
    totalInputTokens: number;
    totalOutputTokens: number;
  };
  byAgentType: Array<{
    agentType: string;
    count: number;
    costUsd: string;
    avgDurationMs: number;
  }>;
  dailyExecutions: Array<{
    date: string;
    total: number;
    completed: number;
    failed: number;
  }>;
  recentFailures: Array<{
    id: string;
    agentType: string;
    errorMessage: string | null;
    organizationName: string;
    createdAt: string;
  }>;
  /**
   * Runs that completed without ever reaching a model. They are status
   * 'completed', so they never appear under recentFailures — which is how a
   * credit outage showed a high fallback rate beside an empty failure list.
   */
  recentDegradations?: Array<{
    id: string;
    agentType: string;
    errorMessage: string | null;
    leadId: string | null;
    organizationName: string;
    createdAt: string;
  }>;
  /**
   * Degradations grouped by classified cause. The provider message carries a
   * per-call request id, so the category prefix is the only usable key.
   */
  degradationsByCause?: Array<{
    cause: string;
    count: number;
    organizations: number;
  }>;
}

export function useAgentLogs(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<AgentLogEntry>>({
    queryKey: ['admin', 'agent-logs', params],
    queryFn: async () => {
      const { data } = await api.get('/agent-logs', { params });
      return data;
    },
  });
}

export function useAgentMetrics(params?: {
  days?: number;
  organizationId?: string;
  agentType?: string;
}) {
  return useQuery<AgentMetrics>({
    queryKey: ['admin', 'agent-logs', 'metrics', params],
    queryFn: async () => {
      const { data } = await api.get('/agent-logs/metrics', { params });
      return data;
    },
    refetchInterval: 30_000,
  });
}

export interface AgentToolActionEntry {
  id: string;
  toolName: string;
  status: string;
  parameters: unknown;
  result: unknown;
  errorMessage: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface AgentExecutionDetail extends AgentLogEntry {
  input: unknown;
  output: unknown;
  metadata: unknown;
  toolActions: AgentToolActionEntry[];
}

export function useAgentExecutionDetail(id: string | null) {
  return useQuery<AgentExecutionDetail>({
    queryKey: ['admin', 'agent-logs', 'detail', id],
    queryFn: async () => {
      const { data } = await api.get(`/agent-logs/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export interface ModelRoute {
  id: string | null;
  taskType: string;
  tier: string;
  provider: string | null;
  model: string | null;
  inputPricePerMTok: string | null;
  outputPricePerMTok: string | null;
  /** Null means the caller's code default applies. */
  temperature: number | null;
  /** Null means the caller's code default applies. */
  maxTokens: number | null;
  plans: string[];
  reason: string | null;
  isActive: boolean;
  version: number;
  /** False when no row is stored: nothing serves this route and calls on it fail. */
  isConfigured: boolean;
  /** Providers this task type's call path can run on. */
  allowedProviders: string[];
  updatedBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export function useModelRoutes() {
  return useQuery<ModelRoute[]>({
    queryKey: ['admin', 'model-routes'],
    queryFn: async () => {
      const { data } = await api.get('/model-routes');
      return data;
    },
  });
}

export function useUpsertModelRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskType,
      tier,
      data,
    }: {
      taskType: string;
      tier: string;
      data: {
        provider: string;
        model: string;
        reason?: string;
        isActive?: boolean;
        /** Null clears the route value back to the code default. */
        temperature?: number | null;
        maxTokens?: number | null;
      };
    }) => {
      return api.put(`/model-routes/${taskType}/${tier}`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'model-routes'] }),
  });
}

export interface BulkModelRouteInput {
  provider: string;
  model: string;
  reason?: string;
  isActive?: boolean;
  /** Omitted keeps each target's stored value; null clears it to the code default. */
  temperature?: number | null;
  maxTokens?: number | null;
  targets: { taskType: string; tier: string }[];
}

export function useBulkUpsertModelRoutes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: BulkModelRouteInput) => {
      const { data: routes } = await api.put('/model-routes', data);
      return routes as ModelRoute[];
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'model-routes'] }),
  });
}

export interface CatalogModel {
  id: string;
  displayName: string;
  /** Null when no default price is known — the admin has to supply one. */
  inputPricePerMTok: number | null;
  outputPricePerMTok: number | null;
  /** `static` = compiled into the backend build, `live` = discovered from the provider API. */
  source: 'static' | 'live';
}

export interface CatalogProvider {
  provider: string;
  models: CatalogModel[];
  syncedAt: string | null;
  syncError: string | null;
}

export interface ModelCatalog {
  providers: CatalogProvider[];
}

export function useModelCatalog() {
  return useQuery<ModelCatalog>({
    queryKey: ['admin', 'model-catalog'],
    queryFn: async () => {
      const { data } = await api.get('/model-catalog');
      return data;
    },
  });
}

export function useRefreshModelCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/model-catalog/refresh');
      return data as ModelCatalog;
    },
    // Write the refreshed catalog straight into the cache: the endpoint already
    // returns it, so refetching would be a second round trip for the same data.
    onSuccess: (data) => queryClient.setQueryData(['admin', 'model-catalog'], data),
  });
}

export function useAgentPrompts() {
  return useQuery<AgentPrompt[]>({
    queryKey: ['admin', 'prompts'],
    queryFn: async () => {
      const { data } = await api.get('/prompts');
      return data;
    },
  });
}

export function useUpdateAgentPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentType,
      data,
    }: {
      agentType: string;
      data: {
        name: string;
        prompt: string;
        description?: string;
        changeReason?: string;
        expectedVersion?: number;
        overrideBlockers?: boolean;
        overrideReason?: string;
      };
    }) => {
      return api.put(`/prompts/${agentType}`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] }),
  });
}

/** One row of the append-only prompt history, without the full prompt body. */
export interface AgentPromptVersionSummary {
  id: string;
  agentType: string;
  version: number;
  name: string;
  description: string | null;
  changeReason: string | null;
  createdBy: string | null;
  createdAt: string;
  promptLength: number;
  promptPreview: string;
}

export interface AgentPromptVersionDetail extends AgentPromptVersionSummary {
  prompt: string;
}

export function useAgentPromptVersions(agentType: string | null) {
  return useQuery<AgentPromptVersionSummary[]>({
    queryKey: ['admin', 'prompts', agentType, 'versions'],
    enabled: Boolean(agentType),
    queryFn: async () => {
      const { data } = await api.get(`/prompts/${agentType}/versions`);
      return data;
    },
  });
}

export function useAgentPromptVersion(agentType: string | null, version: number | null) {
  return useQuery<AgentPromptVersionDetail>({
    queryKey: ['admin', 'prompts', agentType, 'versions', version],
    enabled: Boolean(agentType) && version !== null,
    queryFn: async () => {
      const { data } = await api.get(`/prompts/${agentType}/versions/${version}`);
      return data;
    },
  });
}

export function useRollbackAgentPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentType,
      data,
    }: {
      agentType: string;
      data: { version: number; expectedVersion?: number; changeReason?: string };
    }) => {
      return api.post(`/prompts/${agentType}/rollback`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] }),
  });
}

export interface AgentPromptMigration {
  dirName: string;
  sql: string;
}

export function useGenerateAgentPromptMigration() {
  return useMutation({
    mutationFn: async (agentType: string) => {
      const { data } = await api.post<AgentPromptMigration>(`/prompts/${agentType}/migration`);
      return data;
    },
  });
}

export function useToggleAgentPrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (agentType: string) => {
      return api.patch(`/prompts/${agentType}/toggle`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] }),
  });
}

export type PromptCheckSeverity = 'blocker' | 'high' | 'medium' | 'info';

/**
 * `not_applicable` means the check could not run. It is not a pass, and the
 * editor must never render it as one.
 */
export type PromptCheckStatus = 'pass' | 'fail' | 'warn' | 'not_applicable';

export interface PromptCheckResult {
  id: string;
  title: string;
  severity: PromptCheckSeverity;
  status: PromptCheckStatus;
  message: string;
  suggestion?: string;
  evidence?: string;
}

export interface PromptEvaluation {
  checks: PromptCheckResult[];
  /** Failing blockers only. Judge findings never raise this. */
  blockerCount: number;
  summary: string;
  /** The text that was graded, with the organization's sections appended. */
  composedPrompt: string;
}

/** One blocking defect the save path refused, as returned in the 422 body. */
export interface PromptSaveBlocker {
  id: string;
  title: string;
  message: string;
  suggestion?: string;
}

export function useEvaluateAgentPrompt() {
  return useMutation({
    mutationFn: async ({
      agentType,
      data,
    }: {
      agentType: string;
      data: { prompt: string; organizationId?: string; includeJudge?: boolean };
    }) => {
      const { data: evaluation } = await api.post<PromptEvaluation>(
        `/prompts/${agentType}/evaluate`,
        data,
      );
      return evaluation;
    },
  });
}

export interface AdminToolDefinition {
  id: string;
  name: string;
  displayName: string;
  description: string;
  parametersSchema: Record<string, unknown>;
  handlerKey: string;
  agentTypes: string[];
  organizationId: string | null;
  requiresApproval: boolean;
  isEnabled: boolean;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminToolHandler {
  key: string;
  description: string;
}

export interface AdminToolInput {
  name: string;
  displayName: string;
  description: string;
  parametersSchema: Record<string, unknown>;
  handlerKey: string;
  agentTypes: string[];
  organizationId?: string;
  requiresApproval?: boolean;
  isEnabled?: boolean;
}

export function useAdminTools(organizationId?: string) {
  return useQuery<AdminToolDefinition[]>({
    queryKey: ['admin', 'tools', organizationId ?? null],
    queryFn: async () => {
      const { data } = await api.get('/tools', {
        params: organizationId ? { organizationId } : undefined,
      });
      return data;
    },
  });
}

export function useToolHandlers() {
  return useQuery<AdminToolHandler[]>({
    queryKey: ['admin', 'tools', 'handlers'],
    queryFn: async () => {
      const { data } = await api.get('/tools/handlers');
      return data;
    },
  });
}

export function useCreateAdminTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AdminToolInput) => {
      const { data: created } = await api.post<AdminToolDefinition>('/tools', data);
      return created;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'tools'] }),
  });
}

export function useUpdateAdminTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: AdminToolInput }) => {
      const { data: updated } = await api.put<AdminToolDefinition>(`/tools/${id}`, data);
      return updated;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'tools'] }),
  });
}

export function useToggleAdminTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/tools/${id}/toggle`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'tools'] }),
  });
}

export function useDeleteAdminTool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/tools/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'tools'] }),
  });
}

export interface PlatformSettings {
  billingProvider: string;
  stripeConfigured: boolean;
  usdToCop: string | null;
  defaultMonthlyTokenLimit: number;
  defaultModelTier: string;
  planLimits: Record<
    string,
    {
      maxLeads: number;
      maxSeats: number;
      maxProspectContactsPerCompany: number;
      maxProspectContactCompanies: number;
      modelTier: string;
      voiceEnabled: boolean;
      whatsappEnabled: boolean;
      customPrompts: boolean;
    }
  >;
  supportedModels: AIModel[];
  rateLimits: { ttl: number; limit: number };
}

export interface AIModel {
  key: string;
  provider: string;
  model: string;
  inputPerMTok: number;
  cachedInputPerMTok: number;
  outputPerMTok: number;
  batchInputPerMTok: number;
  batchOutputPerMTok: number;
  isEnabled: boolean;
  source: 'hardcoded' | 'custom';
}

export function useSettings() {
  return useQuery<PlatformSettings>({
    queryKey: ['admin', 'settings'],
    queryFn: async () => {
      const { data } = await api.get('/settings');
      return data;
    },
  });
}

export function useUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: Partial<{
        planLimits: Record<
          string,
          Partial<{
            maxLeads: number;
            maxSeats: number;
            maxProspectContactsPerCompany: number;
            maxProspectContactCompanies: number;
            modelTier: string;
            voiceEnabled: boolean;
            whatsappEnabled: boolean;
            customPrompts: boolean;
          }>
        >;
        rateLimitTtl: number;
        rateLimitMax: number;
        defaultMonthlyTokenLimit: number;
        defaultModelTier: string;
        usdToCop: string;
      }>,
    ) => {
      return api.patch('/settings', data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
      if (variables.usdToCop !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['public-pricing'] });
        queryClient.invalidateQueries({ queryKey: ['billing-checkout-readiness'] });
      }
    },
  });
}

export function useUpsertModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      model: string;
      provider: string;
      providerModelId?: string;
      inputPerMTok: number;
      cachedInputPerMTok?: number;
      outputPerMTok: number;
      batchInputPerMTok?: number;
      batchOutputPerMTok?: number;
      isEnabled?: boolean;
    }) => {
      return api.put('/models', data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });
}

export function useDeleteModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (modelKey: string) => {
      return api.delete(`/models/${encodeURIComponent(modelKey)}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] }),
  });
}

export interface CreateOrganizationPayload {
  name: string;
  slug?: string;
  region?: string;
  country?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
}

export interface UpdateOrganizationPayload {
  name?: string;
  slug?: string;
  isActive?: boolean;
  region?: string;
  country?: string;
  timezone?: string;
  locale?: string;
  currency?: string;
  conversationStack?: 'convai' | 'relay';
}

export interface OrgMember {
  id: string;
  fullName: string;
  email: string;
  role: string;
  lastActiveAt: string | null;
  joinedAt: string;
}

export interface OrgInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  invitedBy: string | null;
  organizationId: string;
  organizationName?: string;
  expiresAt: string;
  createdAt: string;
}

export interface AdminUpdateUserPayload {
  fullName?: string;
  email?: string;
  role?: string;
  platformRole?: string | null;
  isActive?: boolean;
}

export interface ForceAssignPayload {
  organizationId: string;
  role?: string;
}

export interface BulkImportUser {
  email: string;
  fullName: string;
  role?: string;
  organizationId?: string;
  orgRole?: string;
}

export interface BulkImportResult {
  created: number;
  failed: number;
  errors: Array<{ row: number; email: string; reason: string }>;
}

export interface UserDetail extends PlatformUser {
  isActive: boolean;
  auth0Id: string;
}

export function useUserDetail(id: string) {
  return useQuery<UserDetail>({
    queryKey: ['admin', 'users', id],
    queryFn: async () => {
      const { data } = await api.get(`/users/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useOrgMembers(orgId: string) {
  return useQuery<PaginatedResponse<OrgMember>>({
    queryKey: ['admin', 'organizations', orgId, 'members'],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/members`);
      return data;
    },
    enabled: !!orgId,
  });
}

export function useAllInvites(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<OrgInvite>>({
    queryKey: ['admin', 'invites', params],
    queryFn: async () => {
      const { data } = await api.get('/invites', { params });
      return data;
    },
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOrganizationPayload) => {
      return api.post('/organizations', payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] }),
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrganizationPayload }) => {
      return api.patch(`/organizations/${id}`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/organizations/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useExportOrganizationData() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.get(`/organizations/${id}/export`);
      return data;
    },
  });
}

export function usePurgeOrganizationData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/organizations/${id}/data`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useAddOrgMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      role,
    }: {
      orgId: string;
      userId: string;
      role?: string;
    }) => {
      return api.post(`/organizations/${orgId}/members`, { userId, role });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useUpdateOrgMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      userId,
      role,
    }: {
      orgId: string;
      userId: string;
      role: string;
    }) => {
      return api.patch(`/organizations/${orgId}/members/${userId}`, { role });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useRemoveOrgMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, userId }: { orgId: string; userId: string }) => {
      return api.delete(`/organizations/${orgId}/members/${userId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useCreateOrgInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, email, role }: { orgId: string; email: string; role?: string }) => {
      return api.post(`/organizations/${orgId}/invites`, { email, role });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useRevokeOrgInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      return api.delete(`/invites/${inviteId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useUpdateUserFull() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: AdminUpdateUserPayload }) => {
      return api.patch(`/users/${userId}`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      return api.post(`/users/${userId}/reset-password`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      return api.delete(`/users/${userId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useForceAssignToOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: ForceAssignPayload }) => {
      return api.post(`/users/${userId}/force-org-assignment`, data);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

export function useRemoveUserFromOrg() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, orgId }: { userId: string; orgId: string }) => {
      return api.delete(`/users/${userId}/organizations/${orgId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  });
}

// ─── Release versioning hooks (PR5b) ────────────────────────────────────────

export type ReleaseStatus = 'draft' | 'rolling_out' | 'live' | 'rolled_back' | 'superseded';
export type DeployTarget = 'backend' | 'admin_panel' | 'mobile_build' | 'mobile_ota';
export type DeployStatus = 'pending' | 'succeeded' | 'failed' | 'reverted';
export type RolloutEndReason = 'completed' | 'rolled_back' | 'superseded';

export interface Deployment {
  id: string;
  target: DeployTarget;
  status: DeployStatus;
  channel: string | null;
  artifactId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Rollout {
  id: string;
  releaseId: string;
  channel: string;
  percentage: number;
  startedAt: string;
  startedBy: string;
  endedAt: string | null;
  endReason: RolloutEndReason | null;
  endedBy: string | null;
  rollbackRunId: string | null;
  rollbackUrl: string | null;
  autoPromote?: boolean;
  pausedAt?: string | null;
  pauseReason?: string | null;
  nextPromoteAt?: string | null;
}

export interface Release {
  id: string;
  commitSha: string;
  environment: string;
  status: ReleaseStatus;
  deployments: Deployment[];
  rollouts: Rollout[];
  clientReports?: Array<{ id: string }>;
  prLink?: string | null;
  createdAt?: string;
}

export interface ReleasesFilters {
  status?: string;
  environment?: string;
  page?: number;
  limit?: number;
}

export interface RollbackStatus {
  conclusion: string | null;
  status: string;
  polledAt: string;
}

export function useReleases(filters?: ReleasesFilters) {
  return useQuery<PaginatedResponse<Release>>({
    queryKey: ['admin', 'releases', filters],
    queryFn: async () => {
      const { data } = await api.get('/releases', { params: filters });
      return data;
    },
  });
}

export function useRelease(id: string) {
  return useQuery<Release>({
    queryKey: ['admin', 'releases', id],
    queryFn: async () => {
      const { data } = await api.get(`/releases/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export function useActiveRollout(releaseId: string) {
  return useQuery<Rollout | null>({
    queryKey: ['admin', 'releases', releaseId, 'rollout', 'active'],
    queryFn: async () => {
      const { data } = await api.get(`/releases/${releaseId}/rollout/active`);
      return data;
    },
    enabled: !!releaseId,
    refetchInterval: 15_000,
  });
}

export function useStartRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      releaseId,
      percentage,
      channel,
      autoPromote,
    }: {
      releaseId: string;
      percentage: number;
      channel: string;
      autoPromote?: boolean;
    }) => {
      const { data } = await api.post(`/releases/${releaseId}/rollout`, {
        percentage,
        channel,
        ...(autoPromote !== undefined && { autoPromote }),
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'releases', variables.releaseId] });
    },
  });
}

export function useResumeAutoPromotion(releaseId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(`/releases/${releaseId}/rollout/resume`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'releases', releaseId] });
    },
  });
}

export function usePatchRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ releaseId, percentage }: { releaseId: string; percentage: number }) => {
      const { data } = await api.patch(`/releases/${releaseId}/rollout`, { percentage });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'releases', variables.releaseId] });
    },
  });
}

export function useEndRollout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      releaseId,
      outcome,
    }: {
      releaseId: string;
      outcome: RolloutEndReason;
    }) => {
      const { data } = await api.post(`/releases/${releaseId}/rollout/end`, { outcome });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'releases', variables.releaseId] });
    },
  });
}

/**
 * Surfaces the admin panel can roll back. mobile_binary is intentionally
 * excluded — native binaries cannot be rolled back from the API (the user
 * must halt phased release in App Store/Play Console manually).
 */
export const ROLLBACK_SURFACES = ['backend', 'admin_panel', 'mobile_ota'] as const;
export type RollbackSurface = (typeof ROLLBACK_SURFACES)[number];

/**
 * @deprecated Kept temporarily as a re-export for any consumer still
 * importing the old name. New callers MUST pass a single surface via
 * `useRollback.mutate({ surfaces: ['backend'] })`. The backend
 * RollbackService only consumes `surfaces[0]` in V1, so passing more than
 * one was a UI lie. See R4.
 */
export const DEFAULT_ROLLBACK_SURFACES = ROLLBACK_SURFACES;

/**
 * R4 — Initiates a rollback for a SINGLE surface. The backend
 * RollbackService.initiateRollback only consumes `surfaces[0]` in V1, so
 * defaulting to all three surfaces (the previous behavior) would silently
 * roll back only `backend` and lie to the operator. The caller MUST select
 * exactly one surface in the admin UI.
 */
export function useRollback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      releaseId,
      targetReleaseId,
      surfaces,
    }: {
      releaseId: string;
      targetReleaseId: string;
      surfaces: [RollbackSurface];
    }) => {
      const { data } = await api.post(`/releases/${releaseId}/rollback`, {
        targetReleaseId,
        surfaces,
      });
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'releases', variables.releaseId] });
    },
  });
}

/**
 * C7 — Returns the prior live release (most recent live release with
 * createdAt < current.createdAt, same environment) or null if none exists.
 * Used by ReleaseDetail to auto-derive the rollback target.
 */
export function usePreviousLiveRelease(currentReleaseId: string) {
  return useQuery<Release | null>({
    queryKey: ['admin', 'releases', currentReleaseId, 'previous-live'],
    queryFn: async () => {
      try {
        const { data } = await api.get(`/releases/${currentReleaseId}/previous-live`);
        return data;
      } catch (err: unknown) {
        // 404 is a normal "no prior live release" signal — surface as null.
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!currentReleaseId,
  });
}

export function useRollbackStatus(releaseId: string) {
  return useQuery<RollbackStatus>({
    queryKey: ['admin', 'releases', releaseId, 'rollback', 'status'],
    queryFn: async () => {
      const { data } = await api.get(`/releases/${releaseId}/rollback/status`);
      return data;
    },
    enabled: !!releaseId,
    refetchInterval: 10_000,
  });
}

export function useBulkImportUsers() {
  const queryClient = useQueryClient();
  return useMutation<{ data: BulkImportResult }, unknown, { users: BulkImportUser[] }>({
    mutationFn: async (payload) => {
      return api.post('/users/bulk-import', payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// Chat HITL PendingToolAction (backend-nest/src/admin/admin-chat-pending-actions.controller.ts),
// distinct from the legacy per-execution AgentToolAction shape it replaces.
// No precomputed agentType/organizationName/leadName — the new model doesn't
// carry an AgentExecution/Organization/Lead join, so the UI derives display
// text from toolName/organizationId/conversationId/input directly.
export interface PendingToolAction {
  id: string;
  toolName: string;
  input: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  organizationId: string;
  conversationId: string;
  expiresAt: string;
  createdAt: string;
}

export function usePendingToolActions(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<PendingToolAction>>({
    queryKey: ['admin', 'chat-pending-actions', params],
    queryFn: async () => {
      const { data } = await api.get('/chat/pending-actions', { params });
      return data;
    },
    refetchInterval: 15_000,
  });
}

export function useApprovePendingToolAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.post(`/chat/pending-actions/${id}/approve`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'chat-pending-actions'] }),
  });
}

export function useRejectPendingToolAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return api.post(`/chat/pending-actions/${id}/reject`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'chat-pending-actions'] }),
  });
}

export interface UnassignedLead {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  source: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  organizationId: string;
  organizationName: string;
  stageName: string;
  stageColor: string | null;
}

export function useUnassignedLeads(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<UnassignedLead>>({
    queryKey: ['admin', 'unassigned-leads', params],
    queryFn: async () => {
      const { data } = await api.get('/leads/unassigned', { params });
      return data;
    },
  });
}

export interface StageAgentConfig {
  id: string;
  agentType: string;
  isEnabled: boolean;
  customPrompt: string | null;
  priority: number;
}

export interface PipelineStage {
  id: string;
  name: string;
  position: number;
  color: string | null;
  leadCount: number;
  agents: StageAgentConfig[];
}

export interface PipelineOrganization {
  id: string;
  name: string;
  activeMembers: number;
  activeAgents: number;
  totalLeads: number;
  template: {
    id: string;
    name: string;
    stages: PipelineStage[];
  } | null;
}

export function usePipelineAgents(organizationId?: string) {
  return useQuery<PipelineOrganization[]>({
    queryKey: ['admin', 'pipeline-agents', organizationId],
    queryFn: async () => {
      const params = organizationId ? { organizationId } : {};
      const { data } = await api.get('/pipeline-agents', { params });
      return data;
    },
  });
}

export function useUpdateStageAgents() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      stageId,
      agents,
    }: {
      orgId: string;
      stageId: string;
      agents: { agentType: string; isEnabled: boolean; customPrompt?: string }[];
    }) => {
      return api.put(`/organizations/${orgId}/stages/${stageId}/agents`, { agents });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'pipeline-agents'] }),
  });
}

export interface AgentPermissionEntry {
  key: string;
  name: string;
  description: string;
  category: string;
  defaultValue: boolean;
  isGranted: boolean;
  hasOverride: boolean;
  tools: string[];
}

export interface AgentPermissionsResponse {
  organizationId: string;
  agentType: string;
  permissions: AgentPermissionEntry[];
}

export function useAgentPermissions(orgId: string, agentType: string) {
  return useQuery<AgentPermissionsResponse>({
    queryKey: ['admin', 'agent-permissions', orgId, agentType],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/agents/${agentType}/permissions`);
      return data;
    },
    enabled: !!orgId && !!agentType,
  });
}

export function useUpdateAgentPermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      agentType,
      permissions,
    }: {
      orgId: string;
      agentType: string;
      permissions: { permission: string; isGranted: boolean }[];
    }) => {
      return api.put(`/organizations/${orgId}/agents/${agentType}/permissions`, {
        permissions,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'agent-permissions'] }),
  });
}

export interface AgentCustomInstructionAuditEntry {
  id: string;
  beforeText: string | null;
  afterText: string;
  source: 'manual' | 'ai_generated' | 'ai_enriched';
  actorUserId: string | null;
  actorName: string;
  createdAt: string;
}

export interface AgentCustomInstructionsResponse {
  organizationId: string;
  agentType: string;
  current: string | null;
  history: AgentCustomInstructionAuditEntry[];
}

export function useAgentCustomInstructions(orgId: string, agentType: string) {
  return useQuery<AgentCustomInstructionsResponse>({
    queryKey: ['admin', 'agent-custom-instructions', orgId, agentType],
    queryFn: async () => {
      const { data } = await api.get(
        `/organizations/${orgId}/agents/${agentType}/custom-instructions`,
      );
      return data;
    },
    enabled: !!orgId && !!agentType,
  });
}

export interface OrgSettingOverride<T> {
  default: T;
  override: T | null;
  effective: T;
}

export interface OrgSettingsOverridesResponse {
  organizationId: string;
  plan: string;
  overrides: {
    maxLeads: OrgSettingOverride<number>;
    maxSeats: OrgSettingOverride<number>;
    maxProspectContactsPerCompany: OrgSettingOverride<number>;
    maxProspectContactCompanies: OrgSettingOverride<number>;
    monthlyCredits: OrgSettingOverride<number>;
    modelTier: OrgSettingOverride<string>;
    voiceEnabled: OrgSettingOverride<boolean>;
    whatsappEnabled: OrgSettingOverride<boolean>;
    customPrompts: OrgSettingOverride<boolean>;
    rateLimitTtl: OrgSettingOverride<number>;
    rateLimitMax: OrgSettingOverride<number>;
    monthlyTokenLimit: OrgSettingOverride<number>;
  };
}

export function useOrgSettingsOverrides(orgId: string) {
  return useQuery<OrgSettingsOverridesResponse>({
    queryKey: ['admin', 'organizations', orgId, 'settings-overrides'],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/settings-overrides`);
      return data;
    },
    enabled: !!orgId,
  });
}

export type PhoneNumberType = 'landline' | 'mobile' | 'toll_free';

export interface OrganizationPhoneNumber {
  id: string;
  organizationId: string;
  provider: string;
  providerPhoneSid: string;
  phoneE164: string;
  country: string;
  phoneType: PhoneNumberType;
  regulatoryBundleId: string | null;
  label: string | null;
  verifiedAt: string | null;
  deactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterOrgPhoneNumberPayload {
  phoneE164: string;
  providerPhoneSid: string;
  country: string;
  phoneType: PhoneNumberType;
  regulatoryBundleId?: string;
  label?: string;
  provider?: string;
}

export interface UpdateOrgPhoneNumberPayload {
  label?: string;
  reactivate?: boolean;
}

export function useOrgPhoneNumbers(orgId: string) {
  return useQuery<OrganizationPhoneNumber[]>({
    queryKey: ['admin', 'organizations', orgId, 'phone-numbers'],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/phone-numbers`);
      return data;
    },
    enabled: !!orgId,
  });
}

export function useRegisterOrgPhoneNumber(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RegisterOrgPhoneNumberPayload) => {
      const { data } = await api.post(`/organizations/${orgId}/phone-numbers`, payload);
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['admin', 'organizations', orgId, 'phone-numbers'],
      }),
  });
}

export function useUpdateOrgPhoneNumber(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrgPhoneNumberPayload }) => {
      const { data: response } = await api.patch(
        `/organizations/${orgId}/phone-numbers/${id}`,
        data,
      );
      return response;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['admin', 'organizations', orgId, 'phone-numbers'],
      }),
  });
}

export function useDeactivateOrgPhoneNumber(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete(`/organizations/${orgId}/phone-numbers/${id}`);
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['admin', 'organizations', orgId, 'phone-numbers'],
      }),
  });
}

export function useUpdateOrgSettingsOverrides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string;
      data: Partial<{
        maxLeads: number | null;
        maxSeats: number | null;
        monthlyCredits: number | null;
        modelTier: string | null;
        voiceEnabled: boolean | null;
        whatsappEnabled: boolean | null;
        customPrompts: boolean | null;
        rateLimitTtl: number | null;
        rateLimitMax: number | null;
        monthlyTokenLimit: number | null;
      }>;
    }) => {
      return api.patch(`/organizations/${orgId}/settings-overrides`, data);
    },
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({
        queryKey: ['admin', 'organizations', variables.orgId, 'settings-overrides'],
      }),
  });
}

export interface OrgOnboardingProfile {
  industry: string | null;
  productSummary: string | null;
  salesMotion: string | null;
  targetAudience: string | null;
  tone: string | null;
  website: string | null;
  languages: string[];
}

export interface OrgOnboardingPlanAgent {
  type: string;
  enabled: boolean;
  customInstructions?: string;
  rationale: string;
}

export interface OrgOnboardingPlanSkill {
  name: string;
  enabled: boolean;
  rationale: string;
}

export interface OrgOnboardingPlan {
  agents: OrgOnboardingPlanAgent[];
  skills: OrgOnboardingPlanSkill[];
  toolsPreview: string[];
  voiceAgent: {
    useCase: string;
    name: string;
    successCriteria: string[];
  } | null;
  context: {
    industry: string;
    productSummary: string;
    salesMotion: string;
    targetAudience: string;
    tone: string;
  };
  confidence: number;
}

export type UpdateOrgOnboardingProfilePayload = Partial<OrgOnboardingProfile>;

export function useOrgOnboardingProfile(orgId: string) {
  return useQuery<OrgOnboardingProfile>({
    queryKey: ['admin', 'organizations', orgId, 'onboarding-profile'],
    queryFn: async () => {
      const { data } = await api.get(`/organizations/${orgId}/onboarding/profile`);
      return data;
    },
    enabled: !!orgId,
  });
}

export function useUpdateOrgOnboardingProfile(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (profile: UpdateOrgOnboardingProfilePayload) => {
      const { data } = await api.patch(`/organizations/${orgId}/onboarding/profile`, profile);
      return data as OrgOnboardingProfile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['admin', 'organizations', orgId, 'onboarding-profile'], data);
    },
  });
}

export function useGenerateOrgOnboardingPlan(orgId: string) {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/organizations/${orgId}/onboarding/generate`);
      return data as OrgOnboardingPlan;
    },
  });
}

export function useApplyOrgOnboardingPlan(orgId: string) {
  return useMutation({
    mutationFn: async (plan: OrgOnboardingPlan) => {
      const { data } = await api.post(`/organizations/${orgId}/onboarding/apply`, { plan });
      return data;
    },
  });
}

// Chat orchestrator admin surface (FLOSEAI-164 Part A). Read-only over the
// agent_executions ledger (agentType='orchestrator'); cost figures here are
// LLM cost only — WhatsApp message costs live in message_logs and are not
// included (see AdminChatService.getOverview costNote / D9).
export interface ChatToolBreakdownEntry {
  toolName: string;
  count: number;
}

export interface ChatOverviewData {
  runCount: number;
  successCount: number;
  failCount: number;
  costUsd: string;
  costNote: string;
  totalTokens: number;
  avgDurationMs: number | null;
  childCount: number;
  toolsBreakdown: ChatToolBreakdownEntry[];
}

export function useChatOverview(params?: {
  organizationId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<ChatOverviewData>({
    queryKey: ['admin', 'chat', 'overview', params],
    queryFn: async () => {
      const { data } = await api.get('/chat/overview', { params });
      return data;
    },
  });
}

export interface ChatRunEntry {
  id: string;
  organizationId: string;
  status: string;
  costUsd: string;
  inputTokens: number;
  outputTokens: number;
  durationMs: number | null;
  errorMessage: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export function useChatRuns(params?: {
  organizationId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery<PaginatedResponse<ChatRunEntry>>({
    queryKey: ['admin', 'chat', 'runs', params],
    queryFn: async () => {
      const { data } = await api.get('/chat/runs', { params });
      return data;
    },
  });
}

export interface ChatToolCallEntry {
  id: string;
  status: string;
  durationMs: number | null;
  errorMessage: string | null;
  metadata: { toolName?: string } | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface ChatMessageEntry {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  agentType: string | null;
  createdAt: string;
}

export interface ChatRunDetailData {
  run: ChatRunEntry;
  toolCalls: ChatToolCallEntry[];
  messages: ChatMessageEntry[];
}

export function useChatRunDetail(id: string | null) {
  return useQuery<ChatRunDetailData>({
    queryKey: ['admin', 'chat', 'runs', id],
    queryFn: async () => {
      const { data } = await api.get(`/chat/runs/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

export interface ChatAuditEntry {
  type: 'tool_call' | 'approval_resolution';
  id: string;
  organizationId: string;
  toolName: string | null;
  status: string;
  timestamp: string;
}

export function useChatAuditLog(params?: {
  organizationId?: string;
  startDate?: string;
  endDate?: string;
}) {
  return useQuery<{ data: ChatAuditEntry[] }>({
    queryKey: ['admin', 'chat', 'audit-log', params],
    queryFn: async () => {
      const { data } = await api.get('/chat/audit-log', { params });
      return data;
    },
  });
}

export interface ChatSseHealthData {
  subscribeCount: number;
  readyTimeoutCount: number;
  closedChannelCount: number;
}

export function useChatSseHealth() {
  return useQuery<ChatSseHealthData>({
    queryKey: ['admin', 'chat', 'sse-health'],
    queryFn: async () => {
      const { data } = await api.get('/chat/sse-health');
      return data;
    },
    refetchInterval: 60_000,
  });
}

// Admin workflow monitoring (enrichment + voice), normalized cross-org view.
// Mirrors AgentLogEntry/AgentMetrics conventions: PaginatedResponse<WorkflowRow>
// for the list, refetchInterval: 30_000 for both list and metrics.
export type AdminWorkflowSource = 'enrichment' | 'voice';
export type AdminWorkflowNormalizedStatus =
  | 'running'
  | 'succeeded'
  | 'partial'
  | 'failed'
  | 'cancelled';

export interface WorkflowRow {
  id: string;
  source: AdminWorkflowSource;
  workflowType: string;
  organizationId: string;
  organizationName: string;
  status: AdminWorkflowNormalizedStatus;
  rawStatus: string;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  itemsAffected: number;
  errorMessage: string | null;
  metadata: Record<string, unknown>;
}

export interface WorkflowMetrics {
  period: { days: number; startDate: string };
  summary: {
    total: number;
    succeeded: number;
    failed: number;
    running: number;
    partial: number;
    cancelled: number;
    successRate: number;
    avgDurationMs: number;
  };
  bySource: Array<{ source: AdminWorkflowSource; count: number }>;
  byStatus: Array<{ status: AdminWorkflowNormalizedStatus; count: number }>;
}

export function useAdminWorkflows(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<WorkflowRow>>({
    queryKey: ['admin', 'workflows', params],
    queryFn: async () => {
      const { data } = await api.get('/workflows', { params });
      return data;
    },
    refetchInterval: 30_000,
  });
}

export function useAdminWorkflowMetrics(params?: { days?: number }) {
  return useQuery<WorkflowMetrics>({
    queryKey: ['admin', 'workflows', 'metrics', params],
    queryFn: async () => {
      const { data } = await api.get('/workflows/metrics', { params });
      return data;
    },
    refetchInterval: 30_000,
  });
}

// admin-voice-call-monitoring: cross-org, read-only voice call monitoring.
// Source of truth for the 16 VoiceCallStatus values is
// backend-nest/prisma/schema.prisma (VoiceCallStatus enum) — do not guess.
export type VoiceCallStatus =
  | 'scheduled'
  | 'context_loaded'
  | 'dialing'
  | 'detecting'
  | 'disclosure'
  | 'discovery'
  | 'handling'
  | 'cta'
  | 'closing'
  | 'post_processing'
  | 'completed'
  | 'no_answer'
  | 'voicemail'
  | 'hung_up_by_lead'
  | 'technical_failure'
  | 'cancelled';

// Non-terminal statuses — a call in one of these is still in flight, so list
// rows / detail pages showing it should poll for updates.
export const ACTIVE_VOICE_CALL_STATUSES: VoiceCallStatus[] = [
  'scheduled',
  'context_loaded',
  'dialing',
  'detecting',
  'disclosure',
  'discovery',
  'handling',
  'cta',
  'closing',
  'post_processing',
];

const VOICE_CALL_POLL_INTERVAL_MS = 10_000;

export interface AdminVoiceCallRow {
  id: string;
  organizationId: string;
  organization?: { id: string; name: string } | null;
  voiceAgentId: string;
  voiceAgent?: { id: string; name: string; useCase: string } | null;
  leadId: string;
  lead?: { id: string; fullName: string | null; email: string | null; phone: string | null } | null;
  status: VoiceCallStatus;
  outcome: string | null;
  provider: string;
  providerCallSid: string | null;
  fromNumber: string;
  toNumber: string;
  toCountry: string;
  durationSeconds: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
}

export interface AdminVoiceCallStats {
  total: number;
  completed: number;
  scheduled: number;
  inProgress: number;
  noAnswer: number;
  outcomes: Record<string, number>;
  avgDurationSeconds: number;
}

export function useAdminVoiceCalls(params?: Record<string, unknown>) {
  return useQuery<PaginatedResponse<AdminVoiceCallRow>>({
    queryKey: ['admin', 'voice-calls', params],
    queryFn: async () => {
      const { data } = await api.get('/voice-calls', { params });
      return data;
    },
    refetchInterval: (query) => {
      const rows = query.state.data?.data ?? [];
      const hasActiveCall = rows.some((row) => ACTIVE_VOICE_CALL_STATUSES.includes(row.status));
      return hasActiveCall ? VOICE_CALL_POLL_INTERVAL_MS : false;
    },
  });
}

export function useAdminVoiceCallsStats(organizationId?: string) {
  return useQuery<AdminVoiceCallStats>({
    queryKey: ['admin', 'voice-calls', 'stats', organizationId],
    queryFn: async () => {
      const { data } = await api.get('/voice-calls/stats', {
        params: organizationId ? { organizationId } : undefined,
      });
      return data;
    },
  });
}

export interface AdminVoiceCallWebhookEvent {
  id: string;
  source: 'twilio_status' | 'convai_post_call' | 'dispatch_failure' | 'relay_dispatch_failure';
  providerCallSid: string | null;
  rawPayload: Record<string, unknown>;
  errorMessage: string | null;
  receivedAt: string;
}

export interface AdminVoiceCallTranscriptEntry {
  id: string;
  turnIndex: number;
  speaker: string;
  text: string;
  startedAtMs: number;
  endedAtMs: number;
  sentiment: string | null;
}

export interface AdminVoiceCallToolCallEntry {
  id: string;
  turnIndex: number;
  toolName: string;
  request: unknown;
  response: unknown;
  status: string;
  latencyMs: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface AdminVoiceCallDetail extends AdminVoiceCallRow {
  triggeringExecutionId: string | null;
  triggeringExecutionType: string | null;
  transcriptEntries: AdminVoiceCallTranscriptEntry[];
  toolCalls: AdminVoiceCallToolCallEntry[];
  webhookEvents: AdminVoiceCallWebhookEvent[];
}

export function useAdminVoiceCallDetail(id: string | null) {
  return useQuery<AdminVoiceCallDetail>({
    queryKey: ['admin', 'voice-calls', id],
    queryFn: async () => {
      const { data } = await api.get(`/voice-calls/${id}`);
      return data;
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && ACTIVE_VOICE_CALL_STATUSES.includes(status)
        ? VOICE_CALL_POLL_INTERVAL_MS
        : false;
    },
  });
}

export interface AdminVoiceCallAudio {
  url: string | null;
  dualChannelUrl: string | null;
  expiresAt: string;
  ttlSeconds: number;
}

export function useAdminVoiceCallAudio(id: string | null) {
  return useQuery<AdminVoiceCallAudio>({
    queryKey: ['admin', 'voice-calls', id, 'audio'],
    queryFn: async () => {
      const { data } = await api.get(`/voice-calls/${id}/audio`);
      return data;
    },
    enabled: !!id,
  });
}

export type RegulatoryDocumentType = 'rfc' | 'tax_id' | 'nom_184' | 'rut' | 'other';
export type RegulatoryBundleStatus = 'uploaded' | 'in_review' | 'approved' | 'rejected';

export interface RegulatoryBundleSubmission {
  id: string;
  organizationId: string;
  country: string;
  documentType: RegulatoryDocumentType;
  fileName: string;
  status: RegulatoryBundleStatus;
  reviewerNote: string | null;
  submittedAt: string;
  reviewedAt: string | null;
}

export function useRegulatoryPendingSubmissions() {
  return useQuery<RegulatoryBundleSubmission[]>({
    queryKey: ['admin', 'regulatory-pending-submissions'],
    queryFn: async () => {
      const { data } = await voiceComplianceApi.get('/regulatory-bundles/submissions/pending');
      return data;
    },
    refetchInterval: 15_000,
  });
}

export interface ReviewRegulatorySubmissionPayload {
  id: string;
  status: 'approved' | 'rejected';
  reviewerNote?: string;
}

export function useReviewRegulatorySubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, reviewerNote }: ReviewRegulatorySubmissionPayload) => {
      return voiceComplianceApi.patch(`/regulatory-bundles/submissions/${id}`, {
        status,
        reviewerNote,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['admin', 'regulatory-pending-submissions'] }),
  });
}

export function useDownloadRegulatorySubmission() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await voiceComplianceApi.get(
        `/regulatory-bundles/submissions/${id}/download-admin`,
      );
      return data as { url: string };
    },
  });
}

// FLOSEAI-367 — the superadmin CRUD for country_language_mapping. The backend
// shipped in PR #138 and the panel never did, so QA searched for the screen
// across four rounds and navigating straight to the route redirected to
// Overview with no message. It also gates FLOSEAI-124: language inference by
// dialing prefix has no source of data without an administrable table.
export interface CountryLanguageMapping {
  countryCode: string;
  language: string;
  countryIso: string;
}

const COUNTRY_LANGUAGE_MAPPINGS_KEY = ['admin', 'country-language-mappings'];

export function useCountryLanguageMappings() {
  return useQuery<CountryLanguageMapping[]>({
    queryKey: COUNTRY_LANGUAGE_MAPPINGS_KEY,
    queryFn: async () => {
      const { data } = await voiceApi.get('/country-language-mappings/admin');
      return data;
    },
  });
}

export function useCreateCountryLanguageMapping() {
  const queryClient = useQueryClient();

  return useMutation<CountryLanguageMapping, Error, CountryLanguageMapping>({
    mutationFn: async (payload) => {
      const { data } = await voiceApi.post('/country-language-mappings', payload);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUNTRY_LANGUAGE_MAPPINGS_KEY });
    },
  });
}

export function useUpdateCountryLanguageMapping() {
  const queryClient = useQueryClient();

  return useMutation<
    CountryLanguageMapping,
    Error,
    { countryCode: string; language?: string; countryIso?: string }
  >({
    mutationFn: async ({ countryCode, ...body }) => {
      const { data } = await voiceApi.patch(
        `/country-language-mappings/${encodeURIComponent(countryCode)}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUNTRY_LANGUAGE_MAPPINGS_KEY });
    },
  });
}

export function useDeleteCountryLanguageMapping() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { countryCode: string }>({
    mutationFn: async ({ countryCode }) => {
      await voiceApi.delete(`/country-language-mappings/${encodeURIComponent(countryCode)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUNTRY_LANGUAGE_MAPPINGS_KEY });
    },
  });
}
